import React, { useState } from 'react';
import { SlidersHorizontal, Link, Eye, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from "./sheet";
import { Dialog, DialogContent } from "./dialog";
import { Button } from "./button";
import { Label } from "./label";
import { Slider } from "./slider";
import { Checkbox } from "./checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./Select";
import { CSS4_COLORS } from './mplcolors';
import { HEATMAP_PALETTE_KEYS, heatmapPaletteSwatch } from './HeatLayer';
import { Bike, Building2, Trees, Mountain, Waves, Droplets, Car, LandPlot } from 'lucide-react';
import { Input } from "./input";

const ACTIVITY_TYPES = ['Run', 'Ride', 'Hike', 'Walk', 'Swim', 'TrailRun', 'VirtualRide'];

const ColorSettingsPopup = ({
  settings,
  onColorSettingChange,
  onActivityTypeSettingChange,
  triggerButton,
  syncOceansWater,
  onSyncToggle,
  onPaperstyleToggle,
  onWatercolorToggle,
  activitySource,
  // When true, the activities card's header omits the on/off checkbox.
  // Used by the map-section side panel where the activity-source picker
  // (Strava / Garmin / Manual / None) already controls visibility, so
  // a second toggle would be redundant and confusing.
  hideActivitiesToggle = false,
  // Optional override for the per-type picker dropdown. When provided,
  // the dropdown lists only these types (typically the types currently
  // present in the visible activities). When omitted, the static
  // ACTIVITY_TYPES list is used.
  availableActivityTypes,
  selectedCategory: selectedCategoryProp,
  onSelectedCategoryChange,
  onShowAddActivities,
}) => {
  const categories = ['activities', 'land', 'buildings', 'streets', 'forests', 'naturalareas', 'oceans', 'water'];
  const [sync, setSync] = useState(false || syncOceansWater);
  const [activityTab, setActivityTab] = useState(ACTIVITY_TYPES[0]);
  const [selectedCategoryLocal, setSelectedCategoryLocal] = useState(null);
  const [postProcessPreviewId, setPostProcessPreviewId] = useState(null);
  const selectedCategory = selectedCategoryProp !== undefined ? selectedCategoryProp : selectedCategoryLocal;
  const setSelectedCategory = onSelectedCategoryChange || setSelectedCategoryLocal;
  const resolveHex = (colorStr) => {
    if (!colorStr) return '#cccccc';
    if (colorStr.startsWith('#')) return colorStr;
    return CSS4_COLORS[colorStr] || '#cccccc';
  };

  // The header background is drawn semi-transparent on a white card; compute
  // the blended color for a contrast check so text is readable even when alpha
  // is low (otherwise white-on-nearly-white).
  const getHeaderColors = (hex, alpha) => {
    const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
    const a = Math.max(0, Math.min(1, typeof alpha === 'number' ? alpha : 1));
    if (!m) return { bg: hex, fg: '#111827' };
    const r = parseInt(m[1], 16);
    const g = parseInt(m[2], 16);
    const b = parseInt(m[3], 16);
    const bg = `rgba(${r}, ${g}, ${b}, ${a})`;
    const br = r * a + 255 * (1 - a);
    const bgBl = g * a + 255 * (1 - a);
    const bb = b * a + 255 * (1 - a);
    const lum = (0.299 * br + 0.587 * bgBl + 0.114 * bb) / 255;
    return { bg, fg: lum > 0.6 ? '#111827' : '#ffffff' };
  };

  const categoryIcons = {
    activities: Bike,
    land: LandPlot,
    buildings: Building2,
    forests: Trees,
    naturalareas: Mountain,
    oceans: Waves,
    water: Droplets,
    streets: Car,
  };

  // Opacity "sweet spots" per category — rendered as a subtle green band on
  // the opacity slider so the user can see the recommended range at a glance.
  // Rationale: too-high land/forest coverage hides the topography underneath.
  const opacityOptimalRanges = {
    activities: [50, 100],
    land: [5, 60],
    forests: [5, 45],
    oceans: [10, 100],
    water: [10, 100],
    buildings: [5, 100],
    streets: [10, 80],
  };

  const handleCustomColorClick = (category) => {
    const input = document.createElement('input');
    input.type = 'color';
    input.value = settings[category].color;
    input.addEventListener('change', (e) => {
      onColorSettingChange(category, 'color', e.target.value);
      if (sync && category === 'oceans') {
        onColorSettingChange('water', 'color', e.target.value);
      }
    });
    input.click();
  };

  const getDisplayValue = (color) => {
    if (color && color.startsWith('#')) return `custom: ${color}`;
    return color;
  };

  const handleSliderChange = (category, value) => {
    onColorSettingChange(category, 'alpha', value[0] / 100);
    if (sync && category === 'oceans') {
      onColorSettingChange('water', 'alpha', value[0] / 100);
    }
  };

  const handleOpacityInputChange = (category, value) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      onColorSettingChange(category, 'alpha', numValue / 100);
      if (sync && category === 'oceans') {
        onColorSettingChange('water', 'alpha', numValue / 100);
      }
    }
  };

  const handleColorChange = (category, color) => {
    onColorSettingChange(category, 'color', color);
    if (sync && category === 'oceans') {
      onColorSettingChange('water', 'color', color);
    }
  };

  // Open a native color picker that writes back through an arbitrary setter —
  // used for per-activity-type tabs so the "custom" option persists to byType.
  const openCustomColorPicker = (initialColor, setColor) => {
    const input = document.createElement('input');
    input.type = 'color';
    input.value = initialColor && initialColor.startsWith('#') ? initialColor : '#ff0000';
    input.addEventListener('change', (e) => setColor(e.target.value));
    input.click();
  };

  // Render the shared color / opacity / line-width controls from a resolved
  // scope and a single write function. Used for both the common activities
  // settings and per-type overrides.
  const renderActivityStyleControls = ({
    color, alpha, onChange,
    // Two distinct slots so the header-level controls (the "Color
    // types" button) and the picker-level controls (the per-type pill)
    // can sit in different rows. `rightSlot` is kept as a fallback
    // alias for callers that still pass a single slot.
    headerRightSlot = null,
    selectRightSlot = null,
    rightSlot = null,
  }) => {
    const headerSlot = headerRightSlot ?? rightSlot;
    return (
      <div className="space-y-1 lg:space-y-3">
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-md">Color</Label>
            {headerSlot}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <Select
                value={color}
                onValueChange={(value) => {
                  if (value === 'custom') {
                    openCustomColorPicker(color, (c) => onChange('color', c));
                    return;
                  }
                  onChange('color', value);
                }}
              >
                <SelectTrigger className="bg-white border border-none rounded-lg h-9 text-sm">
                  <SelectValue>
                    <div className="flex items-center">
                      <div className="w-4 h-4 mr-2 rounded-full" style={{ backgroundColor: color }} />
                      {getDisplayValue(color)}
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <div className="max-h-60 lg:max-h-80 overflow-y-auto">
                    <SelectItem
                      value="custom"
                      className="sticky top-0 z-10 bg-white border-b border-gray-200"
                    >
                      <div className="flex items-center">
                        <div
                          className="w-4 h-4 mr-2 rounded-full"
                          style={{ background: 'linear-gradient(to right, red,orange,yellow,green,blue,indigo,violet)' }}
                        />
                        custom
                      </div>
                    </SelectItem>
                    {Object.keys(CSS4_COLORS).map((c) => (
                      <SelectItem key={c} value={c}>
                        <div className="flex items-center">
                          <div className="w-4 h-4 mr-2 rounded-full" style={{ backgroundColor: c }} />
                          {c}
                        </div>
                      </SelectItem>
                    ))}
                  </div>
                </SelectContent>
              </Select>
            </div>
            {selectRightSlot}
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex items-baseline justify-between gap-2">
            <Label className="text-xs">Opacity ({Math.round(alpha * 100)}%)</Label>
            {opacityOptimalRanges.activities && (
              <span className="text-xs text-green-700">
                Optimal: {opacityOptimalRanges.activities[0]}–{opacityOptimalRanges.activities[1]}%
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-grow relative">
              {opacityOptimalRanges.activities && (
                <div
                  className="pointer-events-none absolute top-1/2 -translate-y-1/2 h-7 bg-green-400/70 rounded-md z-0 ring-1 ring-green-500/60"
                  style={{
                    left: `${opacityOptimalRanges.activities[0]}%`,
                    width: `${opacityOptimalRanges.activities[1] - opacityOptimalRanges.activities[0]}%`,
                  }}
                  aria-hidden="true"
                />
              )}
              <Slider
                value={[alpha * 100]}
                min={0}
                max={100}
                step={1}
                onValueChange={(value) => onChange('alpha', value[0] / 100)}
                className="relative z-10 border-2 border-white rounded-lg"
              />
            </div>
            <Input
              type="number"
              min={0}
              max={100}
              value={Math.round(alpha * 100)}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (!isNaN(n) && n >= 0 && n <= 100) onChange('alpha', n / 100);
              }}
              className="w-14 h-8 text-center text-sm"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderActivityBody = () => {
    const activities = settings.activities || {};
    const isHeatmap = activities.mode === 'heatmap';
    const byTypeEnabled = !isHeatmap && !!activities.byTypeEnabled;

    // Heatmap mode renders all activities into a single frequency
    // surface, so per-type colors / line widths / by-type overrides
    // have no effect on the output. Hide them and show only the two
    // controls that DO apply:
    //   - a small picker over a curated set of heatmap palettes
    //   - the layer opacity slider (writes to activities.alpha).
    if (isHeatmap) {
      const currentKey = (() => {
        const c = String(activities.color || 'orange').toLowerCase();
        return HEATMAP_PALETTE_KEYS.includes(c) ? c : 'orange';
      })();
      const setPalette = (key) => onColorSettingChange('activities', 'color', key);
      const setAlpha = (a) => onColorSettingChange('activities', 'alpha', a);
      const alpha = typeof activities.alpha === 'number' ? activities.alpha : 0.85;
      const alphaPct = Math.round(Math.max(0, Math.min(1, alpha)) * 100);
      return (
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Heatmap palette</Label>
            <div className="grid grid-cols-5 gap-1.5">
              {HEATMAP_PALETTE_KEYS.map((key) => {
                const active = key === currentKey;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPalette(key)}
                    title={key}
                    className={`relative rounded-md overflow-hidden border transition-all ${active
                      ? 'border-blue-500 ring-2 ring-blue-300'
                      : 'border-gray-200 hover:border-gray-400'
                      }`}
                    aria-pressed={active}
                  >
                    <div
                      className="aspect-square w-full"
                      style={{ background: heatmapPaletteSwatch(key) }}
                    />
                    <div className="text-[10px] text-center py-0.5 capitalize bg-white">
                      {key}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Opacity ({alphaPct}%)</Label>
            <div className="flex items-center gap-2">
              <Slider
                value={[alphaPct]}
                min={0}
                max={100}
                step={1}
                onValueChange={(v) => setAlpha(v[0] / 100)}
                className="flex-1 border-2 border-white rounded-lg"
              />
              <Input
                type="number"
                min={0}
                max={100}
                value={alphaPct}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (!isNaN(n) && n >= 0 && n <= 100) setAlpha(n / 100);
                }}
                className="w-14 h-8 text-center text-sm"
              />
            </div>
          </div>
        </div>
      );
    }

    const commonScope = {
      color: activities.color,
      alpha: activities.alpha,
    };

    const commonOnChange = (setting, value) => onColorSettingChange('activities', setting, value);

    const canEditPerType = byTypeEnabled && typeof onActivityTypeSettingChange === 'function';
    // Use the caller-supplied dropdown list (types currently in view)
    // when provided, otherwise the full ACTIVITY_TYPES. Snap the
    // selection to the first available type so a filtered-out tab
    // doesn't leave the picker showing an absent label.
    const dropdownTypes = (Array.isArray(availableActivityTypes) && availableActivityTypes.length > 0)
      ? availableActivityTypes
      : ACTIVITY_TYPES;
    const selectedTab = dropdownTypes.includes(activityTab) ? activityTab : dropdownTypes[0];
    const typeOverrides = activities.byType?.[selectedTab] || {};
    const typeScope = {
      color: typeOverrides.color || activities.color,
      alpha: typeOverrides.alpha != null ? typeOverrides.alpha : activities.alpha,
    };
    const typeOnChange = (setting, value) => onActivityTypeSettingChange(selectedTab, setting, value);

    // Outline-style pill button. Off → neutral "Color types" invite;
    // on → red destructive "Disable color by types ✕" so the user knows
    // they're in the per-type mode and how to leave.
    const perTypeToggle = typeof onActivityTypeSettingChange === 'function' ? (
      <button
        type="button"
        onClick={() => onColorSettingChange('activities', 'byTypeEnabled', !byTypeEnabled)}
        title={byTypeEnabled ? 'Disable per-type coloring' : 'Color activities per type'}
        className={`shrink-0 inline-flex items-center gap-1 rounded-full border h-7 px-2.5 text-[11px] font-medium transition-colors ${byTypeEnabled
          ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100 hover:border-red-400'
          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400'
          }`}
      >
        <span>{byTypeEnabled ? 'Color by types' : 'Color by types'}</span>
        {byTypeEnabled && <X className="h-3 w-3" />}
      </button>
    ) : null;

    // When byType is on, the type picker sits inline above the controls
    // as a pill-style Select. Each option shows a circular swatch in
    // the actual color that type renders with (the per-type override
    // if set, else the common color) so the user can see at a glance
    // what each type will plot as.
    const typeSwatchColor = (type) => {
      const override = activities.byType?.[type]?.color;
      return override || activities.color || '#9ca3af';
    };
    const typePickerSlot = canEditPerType ? (
      <div className="flex items-center gap-2 shrink-0">
        {/* <Label className="text-[11px] leading-tight text-gray-600">Type</Label> */}
        <Select value={selectedTab} onValueChange={setActivityTab}>
          <SelectTrigger className="bg-white !border-gray-200 hover:!border-gray-300 hover:shadow-sm rounded-full h-8 text-xs pl-2 pr-2 w-28 transition-all">
            <SelectValue>
              <span className="flex items-center gap-1.5">
                <span
                  className="h-3.5 w-3.5 rounded-full border border-gray-200 shadow-sm flex-shrink-0"
                  style={{ backgroundColor: typeSwatchColor(selectedTab) }}
                />
                <span className="truncate">{selectedTab}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="rounded-xl shadow-lg border border-gray-200">
            {dropdownTypes.map((type) => {
              const hasOverride = !!activities.byType?.[type];
              return (
                <SelectItem key={type} value={type} className="rounded-lg py-1.5">
                  <span className="flex items-center gap-2">
                    <span
                      className={`h-4 w-4 rounded-full border flex-shrink-0 ${hasOverride ? 'border-gray-400 ring-2 ring-offset-1 ring-gray-200' : 'border-gray-200'}`}
                      style={{ backgroundColor: typeSwatchColor(type) }}
                    />
                    <span>{type}</span>
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    ) : null;

    // The "Color types" / "Disable color by types" pill sits in the
    // header row (next to the "Color" label) so it's always visible at
    // the top, while the per-type picker (visible only when byType is
    // on) sits in the row below to the right of the color Select. Two
    // distinct visual zones for two distinct controls.
    const headerRightSlot = perTypeToggle;
    const selectRightSlot = typePickerSlot;

    return (
      <>
        {canEditPerType ? (
          renderActivityStyleControls({
            ...typeScope,
            onChange: typeOnChange,
            headerRightSlot,
            selectRightSlot,
          })
        ) : (
          renderActivityStyleControls({
            ...commonScope,
            onChange: commonOnChange,
            headerRightSlot,
            selectRightSlot,
          })
        )}
      </>
    );
  };

  const getCategoryMeta = (category) => {
    const isActive = !!settings[category].active;
    const isWaterSynced = sync && category === 'water';
    const isActivitiesDisabled = category === 'activities' && activitySource === 'none';
    const isBuildingsBig = category === 'buildings' && settings.km2 > 15000;
    const isStreetsBig = category === 'streets' && settings.km2 > 15000;
    // Paperstyle and watercolor both force buildings off — same UX as
    // the >15000 km² lock: toggle disabled, expanded tile shows a red
    // warning instead of the color/opacity body, no "turn on the toggle"
    // hint.
    const isBuildingsPaperstyle = category === 'buildings'
      && (!!settings.paperstyle || !!settings.watercolor);
    const blocked = isWaterSynced || isActivitiesDisabled;
    const toggleDisabled = blocked || isBuildingsBig || isStreetsBig || isBuildingsPaperstyle;
    const hex = resolveHex(settings[category].color);
    const showColored = isActive && !blocked;
    const headerColors = showColored
      ? getHeaderColors(hex, settings[category].alpha)
      : { bg: '#9ca3af', fg: '#ffffff' };
    return {
      isActive,
      isWaterSynced,
      isActivitiesDisabled,
      isBuildingsBig,
      isStreetsBig,
      isBuildingsPaperstyle,
      blocked,
      toggleDisabled,
      hex,
      headerBg: headerColors.bg,
      headerFg: headerColors.fg,
    };
  };

  const renderCategoryHeader = (category) => {
    const meta = getCategoryMeta(category);
    const Icon = categoryIcons[category];
    const showLink = sync && (category === 'oceans' || category === 'water');
    return (
      <div
        className="px-2 py-1.5 flex items-center gap-2"
        style={{ backgroundColor: meta.headerBg, color: meta.headerFg }}
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span className="text-sm font-semibold capitalize truncate">{category}</span>
        {showLink && (
          <Link className="h-3.5 w-3.5 flex-shrink-0" title="Linked with water / oceans" />
        )}
        {!(category === 'activities' && hideActivitiesToggle) && (
          <div
            className="ml-auto flex items-center"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={meta.isActive}
              disabled={meta.toggleDisabled}
              onCheckedChange={(checked) => {
                onColorSettingChange(category, 'active', checked);
                if (sync && category === 'oceans') {
                  onColorSettingChange('water', 'active', checked);
                }
              }}
              className="h-5 w-5 rounded-full border-current"
            />
          </div>
        )}
      </div>
    );
  };

  const renderCategoryTile = (category) => {
    const meta = getCategoryMeta(category);

    let titleHint = `Edit ${category}`;
    if (meta.isActivitiesDisabled) titleHint = 'Select an activity source';
    else if (meta.isWaterSynced) titleHint = 'Synced with oceans';
    else if (meta.isBuildingsBig || meta.isStreetsBig) titleHint = 'Area too large';

    const handleClick = () => setSelectedCategory(category);

    return (
      <div
        role="button"
        tabIndex={meta.blocked ? -1 : 0}
        onClick={() => { if (!meta.blocked) handleClick(); }}
        onKeyDown={(e) => {
          if (!meta.blocked && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleClick();
          }
        }}
        className={`h-full flex flex-col overflow-hidden rounded-xl border shadow-sm transition group ${meta.blocked
          ? 'opacity-60 cursor-not-allowed border-gray-300'
          : 'cursor-pointer hover:shadow-md hover:scale-[1.02] border-transparent'
          } ${!meta.isActive && !meta.blocked ? 'saturate-50' : ''}`}
        title={titleHint}
      >
        {renderCategoryHeader(category)}
        <div className={`relative flex-1 aspect-[4/3] bg-gray-100 overflow-hidden ${!meta.isActive ? 'grayscale opacity-80' : ''}`}>
          <img
            src={
              category === 'activities' && settings.activities?.mode === 'heatmap'
                ? '/assets/custom/activities_heatmap.png'
                : `/assets/custom/${category}.png`
            }
            alt={category}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
          />
          {meta.isActivitiesDisabled && onShowAddActivities && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-2 text-center bg-white/80">
              <p className="text-xs font-semibold text-gray-800">
                Need to add activities first
              </p>
              <Button
                variant="outline"
                size="sm"
                className="rounded-2xl whitespace-nowrap pointer-events-auto"
                onClick={(e) => { e.stopPropagation(); onShowAddActivities(); }}
              >
                Show me
              </Button>
            </div>
          )}
          {!meta.blocked && (
            <div className="absolute bottom-2 right-2 bg-white bg-opacity-90 rounded-full p-1.5 shadow-md opacity-80 group-hover:opacity-100 transition-opacity">
              <SlidersHorizontal className="h-3.5 w-3.5 text-gray-700" />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCollapsedTile = (category) => {
    const meta = getCategoryMeta(category);
    const isSelected = selectedCategory === category;
    const handleClick = () => setSelectedCategory(category);
    let titleHint = `Open ${category}`;
    if (meta.isActivitiesDisabled) titleHint = 'Select an activity source';
    else if (meta.isWaterSynced) titleHint = 'Synced with oceans';
    else if (meta.isBuildingsBig || meta.isStreetsBig) titleHint = 'Area too large';

    return (
      <div
        role="button"
        tabIndex={meta.blocked ? -1 : 0}
        onClick={() => { if (!meta.blocked) handleClick(); }}
        onKeyDown={(e) => {
          if (!meta.blocked && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleClick();
          }
        }}
        className={`rounded-xl overflow-hidden border shadow-sm transition ${meta.blocked
          ? 'opacity-60 cursor-not-allowed border-gray-300'
          : 'cursor-pointer hover:shadow-md'
          } ${isSelected ? 'border-gray-900 ring-2 ring-gray-900' : 'border-transparent'} ${!meta.isActive && !meta.blocked ? 'saturate-50' : ''}`}
        title={titleHint}
      >
        {renderCategoryHeader(category)}
      </div>
    );
  };

  const renderExpandedTile = (category) => {
    const meta = getCategoryMeta(category);
    const sizeWarning = meta.isBuildingsBig
      ? 'Buildings disabled for areas bigger than 15000 km²'
      : meta.isStreetsBig
        ? 'Streets disabled for areas bigger than 15000 km²'
        : meta.isBuildingsPaperstyle
          ? (settings.watercolor
            ? 'Buildings disabled while Watercolored is on'
            : 'Buildings disabled while Hand-painted paper look is on')
          : null;

    return (
      <div className="flex flex-col overflow-hidden rounded-xl border border-gray-200 shadow-lg bg-white">
        {renderCategoryHeader(category)}
        <div className="p-3 space-y-2">
          {meta.isWaterSynced && (
            <div className="bg-blue-100 text-blue-800 px-3 py-2 rounded-xl text-sm flex items-center gap-2 justify-center">
              <Link className="h-4 w-4" />
              <span className="font-medium">Synced with oceans — controlled by the oceans category</span>
              <button
                type="button"
                onClick={() => { if (onSyncToggle) onSyncToggle(false); setSync(false); }}
                className="ml-2 text-[11px] underline hover:text-blue-900"
              >
                Unsync
              </button>
            </div>
          )}
          {meta.isActivitiesDisabled && (
            <div className="text-sm text-gray-600 text-center py-4">
              Select an activity source to enable activities.
            </div>
          )}
          {sizeWarning && (
            <div className="text-red-500 text-sm text-center">{sizeWarning}</div>
          )}
          {meta.isActive && !meta.isActivitiesDisabled && !meta.isWaterSynced ? (
            category === 'activities' ? renderActivityBody() : renderNonActivityBody(category)
          ) : (!meta.isActivitiesDisabled && !meta.isWaterSynced && !meta.isBuildingsPaperstyle && (
            // Skip the "turn on the toggle" hint when the toggle is locked
            // off by paperstyle — the user can't turn it on, so the hint
            // would be misleading. The red warning above already explains.
            <div className="text-sm text-gray-500 text-center py-4">
              Turn on the toggle in the header to edit settings.
            </div>
          ))}
        </div>
        {/* In Basic mode the activities tile is rendered inline (not as
            an expanded view inside a category grid), so a "Done" button
            has nothing to dismiss. */}
        {!renderModeIsBasic && (
          <div className="border-t bg-gray-50">
            <Button
              onClick={() => setSelectedCategory(null)}
              className="w-full bg-gray-900 border-0 text-white hover:bg-gray-600 rounded-none"
            >
              Done
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderNonActivityBody = (category) => {
    // Sync chip lives next to the color dropdown for the oceans/water pair
    // so both cards share the same vertical layout as the activities card.
    // Toggling on either copies oceans→water once, so the synced category
    // visually updates immediately even though oceans is the source of
    // truth from this point on.
    const handleSyncToggle = (checked) => {
      if (onSyncToggle) onSyncToggle(checked);
      setSync(checked);
      if (checked) {
        onColorSettingChange('water', 'color', settings.oceans.color);
        onColorSettingChange('water', 'alpha', settings.oceans.alpha);
        onColorSettingChange('water', 'active', settings.oceans.active);
      }
    };
    const syncSlot = (category === 'oceans' || category === 'water') ? (
      <div className="flex items-center gap-1.5 shrink-0">
        <Label htmlFor={`syncCheckbox-${category}`} className="text-[11px] cursor-pointer leading-tight text-right flex items-center gap-1">
          {category === 'oceans' ? 'Sync water' : 'Sync oceans'}
          <Link className="h-3 w-3" />
        </Label>
        <Checkbox
          id={`syncCheckbox-${category}`}
          checked={sync}
          onCheckedChange={handleSyncToggle}
          className="h-5 w-5 rounded-full border-black"
        />
      </div>
    ) : null;

    return (
      <div className="space-y-3">
        <div className="space-y-1">
          <Label className="text-xs">Color</Label>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <Select
                value={settings[category].color}
                onValueChange={(value) => {
                  if (value === 'custom') {
                    handleCustomColorClick(category);
                    return;
                  }
                  handleColorChange(category, value);
                }}
              >
                <SelectTrigger className="bg-white border border-none rounded-lg h-9 text-sm">
                  <SelectValue>
                    <div className="flex items-center">
                      <div
                        className="w-4 h-4 mr-2 rounded-full"
                        style={{ backgroundColor: settings[category].color }}
                      />
                      {getDisplayValue(settings[category].color)}
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <div className="max-h-60 lg:max-h-80 overflow-y-auto">
                    <SelectItem
                      value="custom"
                      className="sticky top-0 z-10 bg-white border-b border-gray-200"
                    >
                      <div className="flex items-center">
                        <div
                          className="w-4 h-4 mr-2 rounded-full"
                          style={{ background: 'linear-gradient(to right, red,orange,yellow,green,blue,indigo,violet)' }}
                        />
                        custom
                      </div>
                    </SelectItem>
                    {Object.keys(CSS4_COLORS).map((color) => (
                      <SelectItem key={color} value={color}>
                        <div className="flex items-center">
                          <div
                            className="w-4 h-4 mr-2 rounded-full"
                            style={{ backgroundColor: color }}
                          />
                          {color}
                        </div>
                      </SelectItem>
                    ))}
                  </div>
                </SelectContent>
              </Select>
            </div>
            {syncSlot}
          </div>
        </div>
        <div className="space-y-1">
          <div className="flex items-baseline justify-between gap-2">
            <Label className="text-xs">Opacity ({Math.round(settings[category].alpha * 100)}%)</Label>
            {opacityOptimalRanges[category] && (
              <span className="text-xs text-green-700">
                Optimal: {opacityOptimalRanges[category][0]}–{opacityOptimalRanges[category][1]}%
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-grow relative">
              {opacityOptimalRanges[category] && (
                <div
                  className="pointer-events-none absolute top-1/2 -translate-y-1/2 h-7 bg-green-400/70 rounded-md z-0 ring-1 ring-green-500/60"
                  style={{
                    left: `${opacityOptimalRanges[category][0]}%`,
                    width: `${opacityOptimalRanges[category][1] - opacityOptimalRanges[category][0]}%`,
                  }}
                  aria-hidden="true"
                />
              )}
              <Slider
                value={[settings[category].alpha * 100]}
                min={0}
                max={100}
                step={1}
                onValueChange={(value) => handleSliderChange(category, value)}
                className="relative z-10 border-2 border-white rounded-lg"
              />
            </div>
            <Input
              type="number"
              min={0}
              max={100}
              value={Math.round(settings[category].alpha * 100)}
              onChange={(e) => handleOpacityInputChange(category, e.target.value)}
              className="w-14 h-8 text-center text-sm"
            />
          </div>
        </div>
      </div>
    );
  };

  // The popup body adapts to settings.renderMode set at the dashboard level:
  //   - 'features' (or undefined): the existing per-category grid + expand
  //     view stays unchanged.
  //   - 'basic': only Activities is customizable, so we render the expanded
  //     activities tile. Tab switching itself happens outside the popup.
  const renderModeIsBasic = settings?.renderMode === 'basic';

  const renderBasicContent = () => (
    <div className="py-2 w-full max-w-xl mx-auto">
      {renderExpandedTile('activities')}
    </div>
  );

  // Top-level post-process style picker. Sits above the per-category
  // grid (below the Back button in the customize sheet) because the
  // chosen style globally changes which per-category controls are
  // available (e.g. Watercolored locks buildings off).
  //
  // Two compact tiles, mutually exclusive — clicking the active tile
  // toggles it off (so there's no need for a dedicated "Standard"
  // option). The eye button on each tile opens a large preview of the
  // image at /assets/post-process/<id>.png.
  const POST_PROCESS_STYLES = [
    { id: 'paperstyle', label: 'Hand-painted paper look', subtitle: '(disables buildings)' },
    { id: 'watercolored', label: 'Watercolored', subtitle: '(disables buildings & topography)' },
  ];

  const renderPostProcessGrid = () => {
    if (!onPaperstyleToggle && !onWatercolorToggle) return null;
    const paperOn = !!settings?.paperstyle;
    const waterOn = !!settings?.watercolor;
    const activeId = waterOn ? 'watercolored' : paperOn ? 'paperstyle' : null;

    const lockBuildings = () => {
      if (onColorSettingChange) onColorSettingChange('buildings', 'active', false);
    };

    const selectStyle = (id) => {
      // Click on already-active tile = turn it off (no Standard tile).
      const next = id === activeId ? null : id;
      if (onPaperstyleToggle) onPaperstyleToggle(next === 'paperstyle');
      if (onWatercolorToggle) onWatercolorToggle(next === 'watercolored');
      if (next) lockBuildings();
    };

    return (
      <div className="mb-3">
        <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5 px-0.5 text-center">
          Post-process
        </div>
        <div className="mx-auto max-w-md grid grid-cols-2 gap-2">
          {POST_PROCESS_STYLES.map((s) => {
            const active = s.id === activeId;
            return (
              <div
                key={s.id}
                className={`relative rounded-lg border transition-colors ${active
                  ? 'border-blue-500 ring-2 ring-blue-300 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-gray-400'
                  }`}
              >
                <button
                  type="button"
                  onClick={() => selectStyle(s.id)}
                  className="w-full text-left px-2.5 py-2 pr-11"
                  aria-pressed={active}
                >
                  <div className={`text-xs font-semibold leading-tight ${active ? 'text-blue-900' : 'text-gray-900'}`}>
                    {s.label}
                  </div>
                  <div className={`text-[11px] mt-0.5 leading-tight ${active ? 'text-blue-700' : 'text-gray-500'}`}>
                    {s.subtitle}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setPostProcessPreviewId(s.id); }}
                  className="absolute top-1/2 -translate-y-1/2 right-1.5 p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                  aria-label={`Preview ${s.label}`}
                  title="Preview"
                >
                  <Eye className="h-6 w-6" />
                </button>
              </div>
            );
          })}
        </div>
        <Dialog
          open={!!postProcessPreviewId}
          onOpenChange={(open) => { if (!open) setPostProcessPreviewId(null); }}
        >
          <DialogContent className="max-w-3xl p-0 overflow-hidden bg-white">
            {postProcessPreviewId && (
              <div className="flex flex-col">
                <img
                  src={`/assets/post-process/${postProcessPreviewId}.png`}
                  alt={`${postProcessPreviewId} preview`}
                  className="w-full h-auto max-h-[80vh] object-contain bg-gray-100"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div className="px-4 py-3 text-sm font-medium border-t border-gray-100">
                  {(POST_PROCESS_STYLES.find((s) => s.id === postProcessPreviewId) || {}).label}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  const renderFeaturesContent = () => {
    if (selectedCategory) {
      // Right column stretches to match the left list (items-stretch) and
      // shares the same gap-2 as the left, so flex 5:3 between card and
      // image lines up the seam with the left's entry/entry gap. Card
      // occupies the top 5 entries, image fills the bottom 3.
      return (
        <div>
          {renderPostProcessGrid()}
          <div className="flex gap-3 py-2 items-stretch">
            <div className="flex flex-col gap-2 w-28 md:w-44 shrink-0">
              {categories.map((c) => (
                <div key={c}>{renderCollapsedTile(c)}</div>
              ))}
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <div className="w-full max-w-xl mx-auto" style={{ flex: '5 1 0' }}>
                {renderExpandedTile(selectedCategory)}
              </div>
              <div
                className="rounded-xl overflow-hidden shadow-sm w-full max-w-xl mx-auto"
                style={{ flex: '3 1 0' }}
              >
                <img
                  src={
                    selectedCategory === 'activities'
                      && settings.activities?.mode === 'heatmap'
                      ? '/assets/custom/activities_heatmap.png'
                      : `/assets/custom/${selectedCategory}.png`
                  }
                  alt=""
                  aria-hidden="true"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div>
        {renderPostProcessGrid()}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 py-2">
          {categories.map((category) => (
            <div key={category}>{renderCategoryTile(category)}</div>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () =>
    renderModeIsBasic ? renderBasicContent() : renderFeaturesContent();

  if (triggerButton) {
    return (
      <Sheet>
        <SheetTrigger asChild>
          <Button
            size="sm"
            className="text-xs lg:text-md rainbow-border-bg text-white hover:bg-gray-600"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Customize
          </Button>
        </SheetTrigger>
        <SheetContent className="w-full overflow-y-auto">
          <SheetHeader>
            {/* <SheetTitle>Color Settings</SheetTitle> */}
          </SheetHeader>
          {renderContent()}
        </SheetContent>
      </Sheet>
    );
  }

  return renderContent();
};

export default ColorSettingsPopup;
